/**
 * racer-engine.ts — pseudo-3D segment-projected racing engine.
 *
 * Ported and substantially extended from the reference Lovable engine
 * (Dodge "Charger Rush"). Adds: NPC traffic with collision, sprite preload
 * with graceful primitive fallback, a much richer 16-bit-style procedural
 * car/billboard draw path. The sprite-artist subagent owns the asset
 * pipeline — see assets/img/sprites/MANIFEST.md.
 */

export type EngineCallbacks = {
  onScore: (score: number) => void;
  onSpeed: (speedKmh: number) => void;
  onCheckpoint: () => void;
  onMiss: (missCount: number) => void;
  onComplete: (finalScore: number) => void;
  onGameOver: (finalScore: number, missCount: number) => void;
  onStage?: (stage: number, theme: StageTheme) => void;
};

export type StageTheme = {
  name: string;
  sky: string;
  horizon: string;
  road: string;
  roadAlt: string;
  grass: string;
  grassAlt: string;
  rumble: string;
  lane: string;
};

type SymbolKind = "tri" | "circ" | "sq" | "x";
type PropVariant = "lamp" | "pine";
type Sprite =
  | { x: number; type: "gate" }
  | { x: number; type: "billboard"; symbol: SymbolKind }
  | { x: number; type: "prop"; variant: PropVariant }
  | { x: number; type: "building"; h: number; w: number; lit: number; tone: number };

// NPC car — spawns ahead of the player, holds its lane, drifts with curve.
// "consumed" flag prevents double-collision on the same NPC.
type NpcCar = {
  z: number;           // world Z position
  x: number;           // lateral offset (-1..1, in road-widths)
  speed: number;       // km/h (constant, slower than max player speed)
  color: number;       // index into NPC_COLORS
  consumed: boolean;
  bumpVX: number;      // transient lateral velocity from a bump (decays)
};

type ScreenPt = { x: number; y: number; w: number };
type CamPt = { x: number; y: number; z: number };
type WorldPt = { x: number; y: number; z: number };
type SegPoint = { world: WorldPt; camera: CamPt; screen: ScreenPt };

type Segment = {
  index: number;
  p1: SegPoint;
  p2: SegPoint;
  curve: number;
  sprites: Sprite[];
  dark: boolean;
};

const ROUND_MS = 30_000;
const STAGE_MS = 15_000;
const ROAD_WIDTH = 2000;
const SEG_LEN = 200;
const DRAW_DIST = 200;
const CAM_HEIGHT = 1000;
const CAM_DEPTH = 0.84;
const MAX_SPEED = SEG_LEN * 60;
const ACCEL = MAX_SPEED / 8;
const OFFROAD_DECEL = -MAX_SPEED / 2;
const CENTRIFUGAL = 0.3;

// NPC traffic
const NPC_SPAWN_GAP = 38;            // segments between NPC spawns
const NPC_BASE_SPEED = MAX_SPEED * 0.55;
const NPC_SPEED_JITTER = MAX_SPEED * 0.15;
const NPC_LANES = [-0.55, -0.18, 0.18, 0.55];   // 4-lane lateral slots
const NPC_COLORS = [
  // [body, body-shadow, accent]
  ["#5b8dee", "#244a96", "#a4c8ff"],   // blue sedan
  ["#f3b85a", "#9a6a18", "#ffe3a4"],   // yellow hatch
  ["#7c4dff", "#3a1a96", "#c8a0ff"],   // purple sport
  ["#e8e3d8", "#9c9486", "#ffffff"],   // bone-white limo
];

export const ROUND_MS_PUBLIC = ROUND_MS;
export const STAGE_MS_PUBLIC = STAGE_MS;

const THEMES: StageTheme[] = [
  { name: "CITY DRIVE",  sky: "#0a1228", horizon: "#3a9cff", road: "#0b0b18", roadAlt: "#141426", grass: "#0a0e1c", grassAlt: "#11162a", rumble: "#6cd6ff", lane: "#ffffff" },
  { name: "GREEN HILLS", sky: "#86c8ff", horizon: "#bce8d0", road: "#1a1d22", roadAlt: "#23272e", grass: "#2f7a3a", grassAlt: "#256033", rumble: "#ffffff", lane: "#ffffff" },
];

// --- sprite preload (graceful — engine works without any sprites) -----
type DrawableSprite = HTMLImageElement | HTMLCanvasElement;
type SpriteMap = Record<string, DrawableSprite>;
const sprites: SpriteMap = {};

// Color tints applied at runtime to the shared car-grey sheet to produce
// NPC traffic variants — keeps download size minimal (one PNG instead of N).
const TRAFFIC_TINTS: Array<{ key: string; color: string }> = [
  { key: "traffic-1", color: "#d23838" },   // red
  { key: "traffic-2", color: "#3a76e0" },   // blue
  { key: "traffic-3", color: "#e0b020" },   // yellow
  { key: "traffic-4", color: "#2f8c44" },   // green
];

const ASSETS: Array<{ key: string; src: string }> = [
  { key: "billboard-tri",  src: "assets/img/playstation-hero.webp" },
  { key: "billboard-circ", src: "assets/img/playstation-hero.webp" },
  { key: "billboard-sq",   src: "assets/img/playstation-hero.webp" },
  { key: "billboard-x",    src: "assets/img/playstation-hero.webp" },
  { key: "player-car",     src: "assets/img/sprites/player-car.png" },
];

function tintCarSprite(src: HTMLImageElement, color: string): HTMLCanvasElement {
  // Tint by stamping the color over the sprite with "color" composite — keeps
  // the original luminance (highlights/shadows) but swaps the hue/saturation.
  // Then mask back to the source alpha so transparent pixels stay transparent.
  const c = document.createElement("canvas");
  c.width = src.naturalWidth;
  c.height = src.naturalHeight;
  const g = c.getContext("2d");
  if (!g) return c;
  g.drawImage(src, 0, 0);
  g.globalCompositeOperation = "color";
  g.fillStyle = color;
  g.fillRect(0, 0, c.width, c.height);
  g.globalCompositeOperation = "destination-in";
  g.drawImage(src, 0, 0);
  return c;
}

function brightenSprite(src: HTMLImageElement, amount = 1.25): HTMLCanvasElement {
  // Pre-baked brightness boost so the player car reads better on dark asphalt.
  const c = document.createElement("canvas");
  c.width = src.naturalWidth;
  c.height = src.naturalHeight;
  const g = c.getContext("2d");
  if (!g) return c;
  g.filter = `brightness(${amount}) saturate(1.05)`;
  g.drawImage(src, 0, 0);
  g.filter = "none";
  return c;
}

export function preloadSprites(): Promise<void> {
  return Promise.all(
    ASSETS.map(
      (a) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => { sprites[a.key] = img; resolve(); };
          // onerror still resolves — missing assets are expected (pending in manifest)
          img.onerror = () => resolve();
          img.src = a.src;
        }),
    ),
  ).then(() => {
    // Once the base car sheet is loaded, generate (a) tinted NPC variants
    // from the *original* grey, and (b) a brightened copy used for the
    // player so the protagonist car reads clearly on dark asphalt.
    //
    // Wrap in try/catch — older Safari may not support
    // ctx.filter or "color" globalCompositeOperation, and a throw here
    // would reject the preload promise and freeze the Start button.
    try {
      const base = sprites["player-car"];
      if (base instanceof HTMLImageElement && base.complete && base.naturalWidth > 0) {
        for (const t of TRAFFIC_TINTS) {
          try { sprites[t.key] = tintCarSprite(base, t.color); } catch (e) { console.warn("tint failed", t.key, e); }
        }
        try { sprites["player-car"] = brightenSprite(base, 1.3); } catch (e) { console.warn("brighten failed", e); }
      }
    } catch (e) {
      console.warn("sprite post-processing failed; falling back to raw sprites", e);
    }
  });
}

function spriteReady(key: string): boolean {
  const s = sprites[key];
  if (!s) return false;
  if (s instanceof HTMLImageElement) return s.complete && s.naturalWidth > 0;
  return true;   // Canvas variants are ready as soon as they're stored
}

const SYMBOL_KEYS: Record<SymbolKind, string> = {
  tri:  "billboard-tri",
  circ: "billboard-circ",
  sq:   "billboard-sq",
  x:    "billboard-x",
};

export function createEngine(canvas: HTMLCanvasElement, cb: EngineCallbacks) {
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  let width = 0, height = 0;

  const segments: Segment[] = [];
  const npcs: NpcCar[] = [];
  let position = 0;
  let playerX = 0;
  let playerBumpVX = 0;   // transient lateral velocity from a collision
  let speed = 0;
  let tilt = 0;
  let nitro = 1;
  let nitroActive = false;
  let score = 0;
  let startedAt = 0;
  let elapsed = 0;
  let running = false;
  let lastFrame = 0;
  let currentStage = -1;
  let misses = 0;
  // Gates encountered so far (passed OR missed — both count as "the
  // 10th pole I crossed"). Drives the speed ramp: cruise cap rises
  // from 100 mph at the start to 150 mph by the 10th gate, +5 mph
  // per gate. Nitro adds +30 mph on top.
  let gatesPassed = 0;

  // Procedural parallax backdrops — drawn once at boot/resize into offscreen
  // canvases, then scrolled horizontally during render() in proportion to
  // forward travel. City for stage 0, hills for stage 1; crossfaded at the
  // stage boundary.
  let bgCity: HTMLCanvasElement | null = null;
  let bgHills: HTMLCanvasElement | null = null;
  const BG_W = 2048;
  const BG_H = 420;

  function resize() {
    const rect = canvas.getBoundingClientRect();
    width = rect.width;
    height = rect.height;
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (!bgCity || !bgHills) buildBackdrops();
  }

  function buildBackdrops() {
    bgCity = makeCityBackdrop();
    bgHills = makeHillsBackdrop();
  }

  function makeCityBackdrop(): HTMLCanvasElement {
    const c = document.createElement("canvas");
    c.width = BG_W;
    c.height = BG_H;
    const g = c.getContext("2d")!;
    // Two-tone silhouette: back row a touch lighter, front row darker.
    // Back row first (deeper into the scene).
    g.fillStyle = "#1a2438";
    let x = 0;
    while (x < BG_W) {
      const w = 70 + Math.floor(Math.random() * 90);
      const h = 140 + Math.floor(Math.random() * 110);
      g.fillRect(x, BG_H - h, w, h);
      x += w + Math.floor(Math.random() * 8);
    }
    // Front row buildings — taller, darker, more variety
    g.fillStyle = "#0a0e1c";
    x = -20;
    while (x < BG_W + 20) {
      const w = 60 + Math.floor(Math.random() * 110);
      const h = 200 + Math.floor(Math.random() * 180);
      g.fillRect(x, BG_H - h, w, h);
      // Antenna on tall buildings (occasional)
      if (h > 320 && Math.random() < 0.55) {
        g.fillRect(x + w / 2 - 1, BG_H - h - 40, 2, 40);
      }
      // Water tower (rare)
      if (Math.random() < 0.06) {
        g.fillRect(x + w * 0.3, BG_H - h - 22, w * 0.4, 18);
        g.fillRect(x + w * 0.45, BG_H - h - 32, w * 0.1, 10);
      }
      x += w + 4 + Math.floor(Math.random() * 18);
    }
    // Window dots — cyan / warm yellow, sparingly so the skyline reads
    // calm rather than busy. Slightly bigger dots so each one feels intentional.
    for (let i = 0; i < 220; i++) {
      g.fillStyle = Math.random() < 0.55 ? "#ffd66a" : "#6cd6ff";
      g.globalAlpha = 0.55 + Math.random() * 0.3;
      g.fillRect(Math.random() * BG_W, BG_H - Math.random() * 300 - 40, 3, 3);
    }
    g.globalAlpha = 1;
    // Seam fix: copy first 40px to the right edge so wrapping is invisible
    g.drawImage(c, 0, 0, 40, BG_H, BG_W - 40, 0, 40, BG_H);
    return c;
  }

  function makeHillsBackdrop(): HTMLCanvasElement {
    const c = document.createElement("canvas");
    c.width = BG_W;
    c.height = BG_H;
    const g = c.getContext("2d")!;
    // Three depth bands of hills, back (palest) to front (darkest).
    const bands = [
      { color: "#8fc99a", amp: 60,  baseY: BG_H * 0.58, freq: 0.012 },
      { color: "#4f9b5c", amp: 95,  baseY: BG_H * 0.72, freq: 0.009 },
      { color: "#2f7a3a", amp: 130, baseY: BG_H * 0.88, freq: 0.0065 },
    ];
    for (const b of bands) {
      g.fillStyle = b.color;
      g.beginPath();
      g.moveTo(0, BG_H);
      const phase = Math.random() * Math.PI * 2;
      for (let xi = 0; xi <= BG_W; xi += 4) {
        const y = b.baseY - b.amp * (0.5 + 0.5 * Math.sin(xi * b.freq + phase) + 0.25 * Math.sin(xi * b.freq * 2.4 + phase * 1.3));
        g.lineTo(xi, y);
      }
      g.lineTo(BG_W, BG_H);
      g.closePath();
      g.fill();
    }
    // Sprinkle pine triangles along the front ridge
    g.fillStyle = "#173d20";
    for (let i = 0; i < 50; i++) {
      const px = Math.random() * BG_W;
      const ph = 16 + Math.random() * 22;
      const py = BG_H * 0.55 + Math.random() * 50;
      g.beginPath();
      g.moveTo(px, py);
      g.lineTo(px + ph * 0.45, py + ph);
      g.lineTo(px - ph * 0.45, py + ph);
      g.closePath();
      g.fill();
    }
    g.drawImage(c, 0, 0, 40, BG_H, BG_W - 40, 0, 40, BG_H);
    return c;
  }

  function drawScrollingStrip(src: HTMLCanvasElement, offsetX: number, y: number, h: number, alpha: number) {
    if (alpha <= 0 || !src) return;
    const stripW = (src.width * h) / src.height;     // preserve aspect at our display height
    // Wrap offsetX into [0, stripW)
    let ox = offsetX % stripW;
    if (ox < 0) ox += stripW;
    ctx!.globalAlpha = alpha;
    let cursor = -ox;
    while (cursor < width) {
      ctx!.drawImage(src, cursor, y, stripW, h);
      cursor += stripW;
    }
    ctx!.globalAlpha = 1;
  }

  function addSegment(curve: number) {
    const n = segments.length;
    segments.push({
      index: n,
      p1: { world: { x: 0, y: 0, z: n * SEG_LEN }, camera: { x: 0, y: 0, z: 0 }, screen: { x: 0, y: 0, w: 0 } },
      p2: { world: { x: 0, y: 0, z: (n + 1) * SEG_LEN }, camera: { x: 0, y: 0, z: 0 }, screen: { x: 0, y: 0, w: 0 } },
      curve,
      sprites: [],
      dark: Math.floor(n / 3) % 2 === 1,
    });
  }

  function easeInOut(a: number, b: number, p: number) {
    return a + (b - a) * ((1 - Math.cos(p * Math.PI)) / 2);
  }

  function addRoad(enter: number, hold: number, leave: number, curve: number) {
    for (let i = 0; i < enter; i++) addSegment(easeInOut(0, curve, i / enter));
    for (let i = 0; i < hold; i++) addSegment(curve);
    for (let i = 0; i < leave; i++) addSegment(easeInOut(curve, 0, i / leave));
  }

  function buildTrack() {
    segments.length = 0;
    npcs.length = 0;
    addRoad(40, 30, 30, 0);
    for (let stage = 0; stage < THEMES.length; stage++) {
      const intensity = 2 + stage * 2.6;
      const tightness = Math.max(6, 30 - stage * 5);
      const sections = 18;   // dense enough for a 30s lap across 2 stages
      for (let i = 0; i < sections; i++) {
        const c = (Math.random() - 0.5) * 2 * intensity;
        addRoad(tightness, tightness, tightness, c);
      }
    }
    addRoad(30, 30, 30, 0);

    // Checkpoint gates — every 60 segments so each one feels deliberate.
    for (let i = 120; i < segments.length; i += 60) {
      segments[i].sprites.push({ x: 0, type: "gate" });
    }
    // Place PS-shape billboards alternating sides — but ONLY along the
    // city stage (first half of the track). The hills stage stays
    // billboard-free so the city→hills transition reads visually.
    const symbols: SymbolKind[] = ["tri", "circ", "sq", "x"];
    const cityEnd = Math.floor(segments.length / 2);
    for (let i = 80, bi = 0; i < cityEnd; i += 60, bi++) {
      const side = bi % 2 === 0 ? -2.4 : 2.4;
      segments[i].sprites.push({ x: side, type: "billboard", symbol: symbols[bi % symbols.length] });
    }

    // City buildings — populate the city stage with depth-staggered
    // silhouettes that project through the painter's queue, so they grow
    // as you approach instead of scrolling as a flat backdrop. Place on
    // both sides at varying lateral offsets so they form a row, not a wall.
    for (let i = 50; i < cityEnd - 20; i += 4) {
      // Two buildings per slot — one on each side. Skip ~10% so the row
      // doesn't feel mechanical.
      if (Math.random() < 0.1) continue;
      const lateralBaseL = -2.6 - Math.random() * 2.4;     // -2.6..-5.0
      const lateralBaseR = 2.6 + Math.random() * 2.4;
      const make = (xLat: number) => ({
        x: xLat,
        type: "building" as const,
        h: 0.55 + Math.random() * 0.55,                    // 0.55..1.10 (taller = farther into sky)
        w: 0.30 + Math.random() * 0.45,                    // 0.30..0.75 (visual width)
        lit: Math.random() < 0.65 ? Math.floor(Math.random() * 14) + 4 : 0, // lit windows
        tone: Math.random(),                               // 0..1 → blend between two dark shades
      });
      segments[i].sprites.push(make(lateralBaseL));
      segments[i].sprites.push(make(lateralBaseR));
      // Occasional water tower / antenna by reusing the same building
      // slot offset slightly closer to the road.
      if (Math.random() < 0.18) {
        const accent = make(lateralBaseL * 0.78);
        accent.h *= 0.45;
        accent.w *= 0.35;
        segments[i].sprites.push(accent);
      }
    }

    // Roadside props — lampposts in stage 0 (city), pines in stage 1 (hills).
    // Stage is determined by the segment's position in the track (first half =
    // city, second half = hills). Alternate sides every spawn.
    const half = segments.length / 2;
    for (let i = 60, pi = 0; i < segments.length; i += 8, pi++) {
      const variant: PropVariant = i < half ? "lamp" : "pine";
      const side = pi % 2 === 0 ? -1.3 : 1.3;
      segments[i].sprites.push({ x: side, type: "prop", variant });
    }
    // Spawn NPC traffic — one car every NPC_SPAWN_GAP segments on a
    // random lane, with random color & speed jitter.
    for (let i = 60; i < segments.length; i += NPC_SPAWN_GAP) {
      npcs.push({
        z: i * SEG_LEN,
        x: NPC_LANES[Math.floor(Math.random() * NPC_LANES.length)],
        speed: NPC_BASE_SPEED + (Math.random() - 0.5) * NPC_SPEED_JITTER,
        color: Math.floor(Math.random() * NPC_COLORS.length),
        consumed: false,
        bumpVX: 0,
      });
    }
  }

  function findSegment(z: number) {
    const idx = Math.floor(z / SEG_LEN) % segments.length;
    return segments[idx < 0 ? idx + segments.length : idx];
  }

  function project(p: SegPoint, camX: number, camY: number, camZ: number) {
    p.camera.x = p.world.x - camX;
    p.camera.y = p.world.y - camY;
    p.camera.z = p.world.z - camZ;
    const scale = CAM_DEPTH / Math.max(p.camera.z, 0.1);
    p.screen.x = Math.round(width / 2 + (scale * p.camera.x * width) / 2);
    p.screen.y = Math.round(height / 2 - (scale * p.camera.y * height) / 2);
    p.screen.w = Math.round((scale * ROAD_WIDTH * width) / 2);
  }

  // Per-NPC ad-hoc projection helper — we don't need a SegPoint struct
  // for these; just (x, y, scale, roadW) to position and size the sprite.
  // `roadW` is the road's projected width at this depth — sprites scale
  // against that so they grow naturally with perspective instead of
  // hitting a hard pixel clamp.
  function projectWorld(wx: number, wy: number, wz: number, camX: number, camY: number, camZ: number) {
    const cz = wz - camZ;
    const scale = CAM_DEPTH / Math.max(cz, 0.1);
    return {
      x: Math.round(width / 2 + (scale * (wx - camX) * width) / 2),
      y: Math.round(height / 2 - (scale * (wy - camY) * height) / 2),
      roadW: Math.round((scale * ROAD_WIDTH * width) / 2),
      scale,
      cz,
    };
  }

  function poly(x1: number, y1: number, x2: number, y2: number, x3: number, y3: number, x4: number, y4: number, color: string) {
    ctx!.fillStyle = color;
    ctx!.beginPath();
    ctx!.moveTo(x1, y1); ctx!.lineTo(x2, y2); ctx!.lineTo(x3, y3); ctx!.lineTo(x4, y4);
    ctx!.closePath(); ctx!.fill();
  }

  function drawSegment(s: Segment, theme: StageTheme) {
    const p1 = s.p1.screen, p2 = s.p2.screen;
    if (p1.y <= p2.y) return;
    const grass = s.dark ? theme.grassAlt : theme.grass;
    const road = s.dark ? theme.roadAlt : theme.road;
    poly(0, p1.y, width, p1.y, width, p2.y, 0, p2.y, grass);
    poly(p1.x - p1.w, p1.y, p1.x + p1.w, p1.y, p2.x + p2.w, p2.y, p2.x - p2.w, p2.y, road);
    const r1 = p1.w / 18, r2 = p2.w / 18;
    poly(p1.x - p1.w - r1, p1.y, p1.x - p1.w, p1.y, p2.x - p2.w, p2.y, p2.x - p2.w - r2, p2.y, theme.rumble);
    poly(p1.x + p1.w + r1, p1.y, p1.x + p1.w, p1.y, p2.x + p2.w, p2.y, p2.x + p2.w + r2, p2.y, theme.rumble);
    if (!s.dark) {
      const l1 = p1.w / 60, l2 = p2.w / 60;
      for (const lane of [-0.33, 0.33]) {
        poly(p1.x + lane * p1.w - l1, p1.y, p1.x + lane * p1.w + l1, p1.y,
             p2.x + lane * p2.w + l2, p2.y, p2.x + lane * p2.w - l2, p2.y, theme.lane);
      }
    }
  }

  function drawGate(seg: Segment, theme: StageTheme) {
    const screen = seg.p1.screen;
    const destW = screen.w * 1.6;        // wider, more arch-like
    const destH = destW * 0.62;          // taller too
    const destX = screen.x - destW / 2;
    const destY = screen.y - destH;
    const beamH = Math.max(6, destH * 0.18);
    const pillarW = Math.max(4, destW * 0.08);

    // Cyan glow halo behind the arch — soft radial wash
    const cx = screen.x;
    const cy = destY + destH * 0.45;
    const haloR = Math.max(destW * 0.55, 24);
    const halo = ctx!.createRadialGradient(cx, cy, haloR * 0.1, cx, cy, haloR);
    halo.addColorStop(0, "rgba(108, 214, 255, 0.55)");
    halo.addColorStop(1, "rgba(108, 214, 255, 0)");
    ctx!.fillStyle = halo;
    ctx!.fillRect(destX - haloR * 0.3, destY - haloR * 0.3, destW + haloR * 0.6, destH + haloR * 0.6);

    // Two pillars — chrome-ish vertical bars
    const pillarGrad = ctx!.createLinearGradient(destX, 0, destX + pillarW, 0);
    pillarGrad.addColorStop(0, "#1c2640");
    pillarGrad.addColorStop(0.5, theme.rumble);
    pillarGrad.addColorStop(1, "#1c2640");
    ctx!.fillStyle = pillarGrad;
    ctx!.fillRect(destX, destY, pillarW, destH);
    ctx!.fillRect(destX + destW - pillarW, destY, pillarW, destH);

    // Top beam — full span, themed rumble color with a dark underline
    ctx!.fillStyle = theme.rumble;
    ctx!.fillRect(destX, destY, destW, beamH);
    ctx!.fillStyle = "rgba(0, 0, 0, 0.45)";
    ctx!.fillRect(destX, destY + beamH - 2, destW, 2);

    // PS shape symbol centered on the beam — cycles through tri/circ/sq/x
    const symbols: SymbolKind[] = ["tri", "circ", "sq", "x"];
    const sym = symbols[(Math.floor(seg.index / 60)) % 4];
    const sx = screen.x;
    const sy = destY + beamH / 2;
    const r = beamH * 0.35;
    ctx!.strokeStyle = "#ffffff";
    ctx!.lineWidth = Math.max(2, r * 0.32);
    ctx!.lineJoin = "round";
    ctx!.lineCap = "round";
    if (sym === "tri") {
      ctx!.beginPath();
      ctx!.moveTo(sx, sy - r);
      ctx!.lineTo(sx + r * 0.95, sy + r * 0.7);
      ctx!.lineTo(sx - r * 0.95, sy + r * 0.7);
      ctx!.closePath();
      ctx!.stroke();
    } else if (sym === "circ") {
      ctx!.beginPath();
      ctx!.arc(sx, sy, r, 0, Math.PI * 2);
      ctx!.stroke();
    } else if (sym === "sq") {
      ctx!.strokeRect(sx - r * 0.9, sy - r * 0.9, r * 1.8, r * 1.8);
    } else {
      ctx!.beginPath();
      ctx!.moveTo(sx - r, sy - r); ctx!.lineTo(sx + r, sy + r);
      ctx!.moveTo(sx + r, sy - r); ctx!.lineTo(sx - r, sy + r);
      ctx!.stroke();
    }
  }

  function drawBillboard(sp: Extract<Sprite, { type: "billboard" }>, screen: ScreenPt) {
    const destW = screen.w * 1.5;
    const destH = destW * (990 / 1760); // match PS hero aspect (16:9)
    const destX = screen.x + screen.w * sp.x - destW / 2;
    const baseY = screen.y;
    const destY = baseY - destH - screen.w * 0.25;

    // Support posts — thick dark wood
    const postW = Math.max(2, destW * 0.06);
    ctx!.fillStyle = "#1a0f06";
    ctx!.fillRect(destX + destW * 0.18, destY + destH, postW, baseY - (destY + destH));
    ctx!.fillRect(destX + destW * 0.78, destY + destH, postW, baseY - (destY + destH));
    // Post highlights
    ctx!.fillStyle = "#3a2510";
    ctx!.fillRect(destX + destW * 0.18, destY + destH, 2, baseY - (destY + destH));
    ctx!.fillRect(destX + destW * 0.78, destY + destH, 2, baseY - (destY + destH));

    // Billboard art — SVG if loaded, else colored-shape primitive fallback
    const key = SYMBOL_KEYS[sp.symbol];
    if (spriteReady(key)) {
      ctx!.drawImage(sprites[key], destX, destY, destW, destH);
    } else {
      drawBillboardPrimitive(sp, destX, destY, destW, destH);
    }
  }

  function drawBillboardPrimitive(
    sp: Extract<Sprite, { type: "billboard" }>,
    x: number, y: number, w: number, h: number,
  ) {
    // Navy frame, colored inner, big shape symbol — fallback styled to
    // match the SVG billboards so swap-in is seamless.
    ctx!.fillStyle = "#1c1f3c";
    ctx!.fillRect(x, y, w, h);
    const innerCol =
      sp.symbol === "tri"  ? "#0ec870" :
      sp.symbol === "circ" ? "#f3635a" :
      sp.symbol === "sq"   ? "#ee5aa0" : "#3a9cff";
    ctx!.fillStyle = innerCol;
    ctx!.fillRect(x + 6, y + 6, w - 12, h - 12);
    ctx!.fillStyle = "#0a0a18";
    ctx!.fillRect(x + 16, y + 16, w - 32, h - 32);

    const cx = x + w / 2;
    const cy = y + h * 0.45;
    const r = Math.min(w, h) * 0.28;
    ctx!.strokeStyle = innerCol;
    ctx!.lineWidth = Math.max(2, r * 0.18);
    if (sp.symbol === "tri") {
      ctx!.beginPath();
      ctx!.moveTo(cx, cy - r);
      ctx!.lineTo(cx + r * 0.9, cy + r * 0.7);
      ctx!.lineTo(cx - r * 0.9, cy + r * 0.7);
      ctx!.closePath();
      ctx!.stroke();
    } else if (sp.symbol === "circ") {
      ctx!.beginPath();
      ctx!.arc(cx, cy, r, 0, Math.PI * 2);
      ctx!.stroke();
    } else if (sp.symbol === "sq") {
      ctx!.strokeRect(cx - r * 0.85, cy - r * 0.85, r * 1.7, r * 1.7);
    } else {
      ctx!.beginPath();
      ctx!.moveTo(cx - r, cy - r); ctx!.lineTo(cx + r, cy + r);
      ctx!.moveTo(cx + r, cy - r); ctx!.lineTo(cx - r, cy + r);
      ctx!.stroke();
    }
  }

  function drawBuilding(sp: Extract<Sprite, { type: "building" }>, screen: ScreenPt) {
    // Only render buildings while we're in the city stage. Hills stage
    // is set-dressed by the bgHills wallpaper, not building sprites.
    if (currentStage >= 1) return;
    // Project from the road-center segment point. Scale against road width
    // so buildings naturally shrink with distance.
    const cx = screen.x + screen.w * sp.x;
    const bw = Math.max(8, screen.w * sp.w);
    const bh = Math.max(20, screen.w * sp.h * 1.8);
    const baseY = screen.y + screen.w * 0.04;            // sit at road shoulder
    const topY = baseY - bh;
    const left = cx - bw / 2;

    // Body — two-tone for cheap depth (front face dark, side a touch lighter).
    const dark = sp.tone < 0.5 ? "#070a16" : "#0c1124";
    const sideTone = sp.tone < 0.5 ? "#1a2236" : "#1e2738";
    // Slim side panel (right edge) — implies the building has a 3rd dimension
    const sideW = Math.max(2, bw * 0.12);
    ctx!.fillStyle = sideTone;
    ctx!.fillRect(left + bw - sideW, topY, sideW, bh);
    ctx!.fillStyle = dark;
    ctx!.fillRect(left, topY, bw - sideW, bh);

    // Roof line — thin highlight so the top reads against the sky
    ctx!.fillStyle = "rgba(108, 214, 255, 0.18)";
    ctx!.fillRect(left, topY, bw, Math.max(1, bh * 0.012));

    // Lit windows — small dots scattered on the front face only
    if (sp.lit > 0 && bw > 22 && bh > 40) {
      const cols = Math.max(2, Math.floor((bw - sideW) / Math.max(6, bw * 0.18)));
      const rows = Math.max(2, Math.floor(bh / Math.max(10, bh * 0.12)));
      const cellW = (bw - sideW) / cols;
      const cellH = bh / rows;
      const dotW = Math.max(1.5, cellW * 0.32);
      const dotH = Math.max(1.5, cellH * 0.34);
      // Seed window pattern off segment-stable randomness so it doesn't
      // flicker per frame. Use position-derived hash.
      let seed = (Math.floor(cx) * 0x9E3779B1 + sp.lit * 0xB5297A4D) | 0;
      const next = () => { seed = (seed * 1664525 + 1013904223) | 0; return ((seed >>> 0) % 1000) / 1000; };
      let placed = 0;
      for (let r = 0; r < rows && placed < sp.lit; r++) {
        for (let c = 0; c < cols && placed < sp.lit; c++) {
          if (next() > 0.45) continue;
          const wx = left + c * cellW + cellW * 0.34;
          const wy = topY + r * cellH + cellH * 0.32;
          ctx!.fillStyle = next() < 0.55 ? "#ffd66a" : "#6cd6ff";
          ctx!.fillRect(wx, wy, dotW, dotH);
          placed++;
        }
      }
    }
  }

  function drawProp(sp: Extract<Sprite, { type: "prop" }>, screen: ScreenPt) {
    // Stage 0 (city) draws no roadside props — lampposts read poorly at
    // speed. Stage 1 draws pines along the hill shoulders.
    if (currentStage <= 0) return;
    const destH = screen.w * 0.28;
    const baseY = screen.y;
    const cx = screen.x + screen.w * sp.x;

    {
      // Pine tree: short trunk + 3 stacked dark-green triangles.
      const trunkW = Math.max(2, destH * 0.10);
      const trunkH = destH * 0.18;
      const trunkX = cx - trunkW / 2;
      const trunkY = baseY - trunkH;
      ctx!.fillStyle = "#3b2410";
      ctx!.fillRect(trunkX, trunkY, trunkW, trunkH);
      // Foliage triangles
      const foliageBaseW = destH * 0.55;
      ctx!.fillStyle = "#173d20";
      for (let i = 0; i < 3; i++) {
        const tierW = foliageBaseW * (1 - i * 0.22);
        const tierH = destH * 0.32;
        const tierBottom = trunkY - i * (tierH * 0.55);
        ctx!.beginPath();
        ctx!.moveTo(cx, tierBottom - tierH);
        ctx!.lineTo(cx + tierW / 2, tierBottom);
        ctx!.lineTo(cx - tierW / 2, tierBottom);
        ctx!.closePath();
        ctx!.fill();
      }
      // Highlight stroke on top triangle
      ctx!.fillStyle = "#2a6b39";
      const hw = foliageBaseW * 0.18;
      ctx!.beginPath();
      ctx!.moveTo(cx, trunkY - destH * 0.86);
      ctx!.lineTo(cx + hw / 2, trunkY - destH * 0.70);
      ctx!.lineTo(cx - hw / 2, trunkY - destH * 0.70);
      ctx!.closePath();
      ctx!.fill();
    }
  }

  function checkGate(seg: Segment, worldX: number) {
    for (const sp of seg.sprites) {
      if (sp.type !== "gate") continue;
      if (sp.x < 90) {
        if (Math.abs(worldX - sp.x) < 0.85) {
          cb.onCheckpoint();
          score += 250;
        } else {
          misses += 1;
          cb.onMiss(misses);
          if (misses >= 3) {
            running = false;
            cb.onGameOver(Math.floor(score), misses);
          }
        }
        // Pass or miss both count as encountering a gate — the speed
        // ramp is about how far you've travelled, not how cleanly.
        gatesPassed += 1;
        sp.x = 99;
      }
    }
  }

  function checkNpcCollision() {
    // Player band: PLAYER_Z ± SEG_LEN. Lateral hit if |playerX - npc.x| <
    // ~0.18 (lane width). Costs a miss + bounces both cars apart.
    const PLAYER_Z = position + SEG_LEN * 2.0;
    for (const n of npcs) {
      if (n.consumed) continue;
      if (Math.abs(n.z - PLAYER_Z) > SEG_LEN * 0.9) continue;
      if (Math.abs(playerX - n.x) < 0.18) {
        n.consumed = true;
        // Bounce apart: kick player away from the NPC, kick NPC the other
        // way. Same sign on both = they separate. Player gets a bigger kick
        // because the NPC is "heavier" / holding its lane.
        const dir = playerX < n.x ? -1 : 1;
        playerBumpVX += dir * 1.8;
        n.bumpVX += -dir * 1.2;
        // Speed dump on impact — feels like a real collision.
        speed = Math.max(MAX_SPEED * 0.3, speed * 0.55);
        misses += 1;
        cb.onMiss(misses);
        if (misses >= 3) {
          running = false;
          cb.onGameOver(Math.floor(score), misses);
        }
      }
    }
  }

  function currentTheme(): StageTheme {
    const stage = Math.max(0, Math.min(THEMES.length - 1, Math.floor(elapsed / STAGE_MS)));
    return THEMES[stage];
  }

  function difficultyFactor(): number {
    const stage = Math.max(0, Math.min(THEMES.length - 1, Math.floor(elapsed / STAGE_MS)));
    return 1 - stage * 0.03;
  }

  function frame(t: number) {
    if (!running) return;
    const dt = Math.max(0, Math.min((t - lastFrame) / 1000, 0.05));
    lastFrame = t;
    elapsed = Math.max(0, t - startedAt);
    if (elapsed >= ROUND_MS) {
      running = false;
      cb.onComplete(Math.floor(score));
      return;
    }

    const stage = Math.max(0, Math.min(THEMES.length - 1, Math.floor(elapsed / STAGE_MS)));
    if (stage !== currentStage) {
      currentStage = stage;
      cb.onStage?.(stage, THEMES[stage]);
    }

    // Speed ramp: cruise cap rises from 1.0× → 1.5× MAX_SPEED across
    // the first 10 gates the player crosses (so the road actually
    // scrolls faster as they progress, not just a HUD-only fake-out).
    // Nitro stacks on top: +0.3× MAX_SPEED whenever it's firing.
    // Display mapping below converts:
    //   speed = 1.0× MAX_SPEED → 100 mph (gate 0 cruise)
    //   speed = 1.5× MAX_SPEED → 150 mph (gate 10+ cruise)
    //   speed = 1.8× MAX_SPEED → 180 mph (gate 10+ + nitro)
    const gateProg = Math.min(10, gatesPassed);
    const cruiseMul = 1 + gateProg * 0.05;   // 1.00 → 1.50
    const nitroMul = nitroActive ? 0.3 : 0;
    const cap = MAX_SPEED * (cruiseMul + nitroMul);
    speed = Math.min(cap, speed + ACCEL * dt);
    if (nitroActive && nitro > 0) nitro = Math.max(0, nitro - dt * 0.5);
    else nitro = Math.min(1, nitro + dt * 0.15);
    if (nitroActive && nitro <= 0) nitroActive = false;

    position = (position + speed * dt) % (segments.length * SEG_LEN);

    // NPCs roll forward at their own speed (looping when they pass the
    // end of the track, so the road always has traffic).
    const trackLen = segments.length * SEG_LEN;
    for (const n of npcs) {
      n.z = (n.z + n.speed * dt) % trackLen;
      // Apply transient bump velocity (from collisions), decay it, clamp
      // the NPC to the road so it can't slide off into infinity.
      if (n.bumpVX !== 0) {
        n.x += n.bumpVX * dt;
        n.bumpVX *= Math.exp(-dt * 5);
        if (Math.abs(n.bumpVX) < 0.02) n.bumpVX = 0;
        n.x = Math.max(-0.9, Math.min(0.9, n.x));
      }
      // Reset "consumed" once an NPC has been pushed far behind the camera
      // (so it's available again on the next lap-wrap).
      if (n.consumed && (n.z - position + trackLen) % trackLen > trackLen / 2) {
        n.consumed = false;
      }
    }

    const seg = findSegment(position);
    if (!seg) { requestAnimationFrame(frame); return; }

    const curveForce = seg.curve * (speed / MAX_SPEED) * CENTRIFUGAL;
    playerX = playerX + tilt * dt * 2.2 - curveForce * dt + playerBumpVX * dt;
    if (playerBumpVX !== 0) {
      playerBumpVX *= Math.exp(-dt * 6);
      if (Math.abs(playerBumpVX) < 0.02) playerBumpVX = 0;
    }
    if (Math.abs(playerX) > 1) {
      speed = Math.max(MAX_SPEED * 0.25, speed + OFFROAD_DECEL * dt);
      playerX = Math.max(-1.2, Math.min(1.2, playerX));
    }

    // Display mph — direct linear map: 1.0× MAX_SPEED reads 100, 1.5×
    // reads 150 (after 10 gates), 1.8× reads 180 (10 gates + nitro).
    // Hard-capped at 180 so an over-cap blip never spikes the HUD.
    // Score keeps using the raw engine ratio so its growth rate is
    // unchanged regardless of where in the ramp the player is.
    const speedMph = Math.min(180, (speed / MAX_SPEED) * 100);
    score += dt * (speed / MAX_SPEED) * 160;
    cb.onScore(Math.floor(score));
    cb.onSpeed(Math.floor(speedMph));

    checkGate(seg, playerX);
    checkNpcCollision();
    render(seg, currentTheme());
    requestAnimationFrame(frame);
  }

  function drawCarShadow(cx: number, cy: number, w: number, h: number) {
    ctx!.fillStyle = "rgba(0, 0, 0, 0.45)";
    ctx!.beginPath();
    ctx!.ellipse(cx, cy, w * 0.46, h, 0, 0, Math.PI * 2);
    ctx!.fill();
  }

  function drawNpcCar(npc: NpcCar, screen: ScreenPt) {
    // Anchor on the segment's projected screen point so NPCs follow the
    // curved road's lateral shift and width. screen.x is road-center at the
    // NPC's depth, screen.w is the projected road width, screen.y is the
    // road surface. The NPC sits in lane npc.x (in road-widths).
    const FRAME_W = 330, FRAME_H = 239;
    const carW = Math.max(24, Math.min(width * 0.55, screen.w * 0.30));
    const carH = carW * (FRAME_H / FRAME_W);   // match sprite aspect
    const sx = screen.x + screen.w * npc.x;
    const sy = screen.y;
    const x = sx - carW / 2;
    const y = sy - carH;
    const [body, shadow, accent] = NPC_COLORS[npc.color];

    const tintKey = `traffic-${(npc.color % TRAFFIC_TINTS.length) + 1}`;
    if (spriteReady(tintKey)) {
      // Ground shadow first
      drawCarShadow(sx, sy - carH * 0.05, carW, carH * 0.13);
      // NPC sprite is the same 990×239 / 3-frame sheet as the player; we
      // always use the center "straight" frame since NPCs hold their lane.
      ctx!.drawImage(
        sprites[tintKey],
        FRAME_W, 0, FRAME_W, FRAME_H,
        x, y, carW, carH,
      );
      return;
    }

    // Procedural rear-view sedan — sized to the projection scale.
    // Ground shadow
    ctx!.fillStyle = "rgba(0,0,0,0.55)";
    ctx!.beginPath();
    ctx!.ellipse(sx, sy + carH * 0.02, carW * 0.52, carH * 0.10, 0, 0, Math.PI * 2);
    ctx!.fill();
    // Body (lower half — darker)
    ctx!.fillStyle = shadow;
    ctx!.fillRect(x + carW * 0.06, y + carH * 0.50, carW * 0.88, carH * 0.42);
    // Body (upper half — lighter)
    ctx!.fillStyle = body;
    ctx!.fillRect(x + carW * 0.10, y + carH * 0.20, carW * 0.80, carH * 0.32);
    // Roof
    ctx!.fillStyle = shadow;
    ctx!.fillRect(x + carW * 0.18, y + carH * 0.08, carW * 0.64, carH * 0.18);
    // Rear window
    ctx!.fillStyle = "#0a0e1a";
    ctx!.fillRect(x + carW * 0.22, y + carH * 0.18, carW * 0.56, carH * 0.18);
    // Bumper
    ctx!.fillStyle = "#0a0a0a";
    ctx!.fillRect(x + carW * 0.04, y + carH * 0.78, carW * 0.92, carH * 0.10);
    // Taillights (red squares, brighter when player is closing distance)
    ctx!.fillStyle = "#ff3a30";
    ctx!.fillRect(x + carW * 0.10, y + carH * 0.62, carW * 0.10, carH * 0.10);
    ctx!.fillRect(x + carW * 0.80, y + carH * 0.62, carW * 0.10, carH * 0.10);
    // Highlight stripe
    ctx!.fillStyle = accent;
    ctx!.fillRect(x + carW * 0.12, y + carH * 0.50, carW * 0.76, carH * 0.04);
  }

  function drawPlayerCar() {
    // 16-bit-style chunky rear-view muscle car. Tilt-aware: when the
    // player steers hard, the car leans visually and the road blur stripe
    // shifts. Sprite-or-primitive (sprite preferred when shipped).
    const baseH = Math.min(height * 0.40, width * 0.40);
    const carH = baseH;
    const lean = tilt * 6;                  // body lean px
    const cx = width / 2 + lean;
    // Anchor the car ~10% higher than the canvas bottom so the rear bumper
    // clears the HUD blocks (km/h, miss, nitro) and reads as a full sprite.
    const cy = height - carH * 0.55 - height * 0.10;

    if (spriteReady("player-car")) {
      // Sheet is 990×239: 3 frames of 330×239 (left-turn / straight / right-turn).
      // The angled turn frames don't match the chase-camera geometry —
      // they read as a wonky 3/4 view rather than a clean tilt. Lock the
      // rear view to the straight frame; the `lean` offset handles the
      // visual side-to-side sway.
      const FRAME_W = 330, FRAME_H = 239;
      const frame = 1;
      const carW = carH * (FRAME_W / FRAME_H);
      // Ground shadow first (under the car, slightly behind/below center)
      drawCarShadow(cx, cy + carH * 0.40, carW, carH * 0.16);
      ctx!.drawImage(
        sprites["player-car"],
        frame * FRAME_W, 0, FRAME_W, FRAME_H,
        cx - carW / 2, cy - carH / 2, carW, carH,
      );
      return;
    }

    const carW = carH * 1.55;

    const x = cx - carW / 2;
    const y = cy - carH / 2;

    // Ground shadow (slightly offset opposite to lean)
    ctx!.fillStyle = "rgba(0,0,0,0.6)";
    ctx!.beginPath();
    ctx!.ellipse(cx - lean * 0.5, cy + carH * 0.46, carW * 0.55, carH * 0.10, 0, 0, Math.PI * 2);
    ctx!.fill();

    // Body — lower (darkest)
    ctx!.fillStyle = "#8b0a1a";
    ctx!.fillRect(x + carW * 0.04, y + carH * 0.62, carW * 0.92, carH * 0.36);
    // Body — mid (mid red)
    ctx!.fillStyle = "#c8102e";
    ctx!.fillRect(x + carW * 0.08, y + carH * 0.36, carW * 0.84, carH * 0.30);
    // Body — top (lighter red highlight)
    ctx!.fillStyle = "#ec3a4f";
    ctx!.fillRect(x + carW * 0.16, y + carH * 0.26, carW * 0.68, carH * 0.14);
    // Roof (dark)
    ctx!.fillStyle = "#640514";
    ctx!.fillRect(x + carW * 0.22, y + carH * 0.18, carW * 0.56, carH * 0.12);
    // Rear window — dark with subtle gradient
    const wgrad = ctx!.createLinearGradient(0, y + carH * 0.20, 0, y + carH * 0.42);
    wgrad.addColorStop(0, "#0a0e1c");
    wgrad.addColorStop(1, "#1a2438");
    ctx!.fillStyle = wgrad;
    ctx!.fillRect(x + carW * 0.26, y + carH * 0.20, carW * 0.48, carH * 0.20);
    // Roof-window edge highlight
    ctx!.fillStyle = "#2a3852";
    ctx!.fillRect(x + carW * 0.26, y + carH * 0.20, carW * 0.48, 2);

    // Hood — center stripe (blue, like a Mustang Cobra)
    ctx!.fillStyle = "#3a9cff";
    ctx!.fillRect(x + carW * 0.46, y + carH * 0.40, carW * 0.08, carH * 0.32);
    ctx!.fillStyle = "#0a3a78";
    ctx!.fillRect(x + carW * 0.46, y + carH * 0.40, carW * 0.08, carH * 0.04);

    // Side window slits (where rear meets quarter panel)
    ctx!.fillStyle = "#0a0e1c";
    ctx!.fillRect(x + carW * 0.12, y + carH * 0.30, carW * 0.10, carH * 0.08);
    ctx!.fillRect(x + carW * 0.78, y + carH * 0.30, carW * 0.10, carH * 0.08);

    // Spoiler — black bar across the trunk
    ctx!.fillStyle = "#0a0a0a";
    ctx!.fillRect(x + carW * 0.18, y + carH * 0.48, carW * 0.64, carH * 0.04);
    // Spoiler shadow
    ctx!.fillStyle = "rgba(0,0,0,0.4)";
    ctx!.fillRect(x + carW * 0.18, y + carH * 0.52, carW * 0.64, carH * 0.02);

    // Rear bumper / trunk line
    ctx!.fillStyle = "#0a0a0a";
    ctx!.fillRect(x + carW * 0.04, y + carH * 0.82, carW * 0.92, carH * 0.06);

    // Taillights — left + right, glow on nitro
    const tailColor = nitroActive ? "#fff43a" : "#ff3a30";
    ctx!.fillStyle = tailColor;
    ctx!.fillRect(x + carW * 0.08, y + carH * 0.70, carW * 0.14, carH * 0.10);
    ctx!.fillRect(x + carW * 0.78, y + carH * 0.70, carW * 0.14, carH * 0.10);
    // Tail light inner glow
    ctx!.fillStyle = nitroActive ? "#ffffff" : "#ffb8a0";
    ctx!.fillRect(x + carW * 0.10, y + carH * 0.72, carW * 0.10, carH * 0.04);
    ctx!.fillRect(x + carW * 0.80, y + carH * 0.72, carW * 0.10, carH * 0.04);

    // License plate (yellowed pixel)
    ctx!.fillStyle = "#fff4a8";
    ctx!.fillRect(x + carW * 0.40, y + carH * 0.74, carW * 0.20, carH * 0.06);
    ctx!.fillStyle = "#1c1f3c";
    ctx!.fillRect(x + carW * 0.42, y + carH * 0.76, carW * 0.16, carH * 0.02);

    // Wheels (rear-view — visible tires + arches)
    ctx!.fillStyle = "#0a0a0a";
    ctx!.fillRect(x + carW * 0.00, y + carH * 0.74, carW * 0.10, carH * 0.22);
    ctx!.fillRect(x + carW * 0.90, y + carH * 0.74, carW * 0.10, carH * 0.22);
    // Wheel rim highlight
    ctx!.fillStyle = "#6a6a6a";
    ctx!.fillRect(x + carW * 0.02, y + carH * 0.80, carW * 0.06, carH * 0.04);
    ctx!.fillRect(x + carW * 0.92, y + carH * 0.80, carW * 0.06, carH * 0.04);

    // Exhaust tips
    ctx!.fillStyle = "#4a4a52";
    ctx!.fillRect(x + carW * 0.32, y + carH * 0.88, carW * 0.08, carH * 0.06);
    ctx!.fillRect(x + carW * 0.60, y + carH * 0.88, carW * 0.08, carH * 0.06);
    // Exhaust flame when nitro is firing
    if (nitroActive && nitro > 0) {
      ctx!.fillStyle = "#3a9cff";
      ctx!.fillRect(x + carW * 0.30, y + carH * 0.92, carW * 0.12, carH * 0.04);
      ctx!.fillRect(x + carW * 0.58, y + carH * 0.92, carW * 0.12, carH * 0.04);
      ctx!.fillStyle = "#ffd166";
      ctx!.fillRect(x + carW * 0.32, y + carH * 0.94, carW * 0.08, carH * 0.02);
      ctx!.fillRect(x + carW * 0.60, y + carH * 0.94, carW * 0.08, carH * 0.02);
    }
  }

  function render(baseSeg: Segment, theme: StageTheme) {
    // Sky gradient
    const grad = ctx!.createLinearGradient(0, 0, 0, height * 0.55);
    grad.addColorStop(0, theme.sky);
    grad.addColorStop(1, theme.horizon);
    ctx!.fillStyle = grad;
    ctx!.fillRect(0, 0, width, height);

    // Distant pixel "sun" on the horizon — extra ambiance
    const sunY = height * 0.45;
    ctx!.fillStyle = theme.rumble;
    ctx!.globalAlpha = 0.5;
    ctx!.beginPath();
    ctx!.arc(width * 0.5, sunY, Math.min(width, height) * 0.08, 0, Math.PI * 2);
    ctx!.fill();
    ctx!.globalAlpha = 1;

    // Stage 1 only — hills wallpaper at the horizon. Stage 0 (city) gets
    // its depth from the per-segment building sprites that project through
    // the painter's queue, so it doesn't need a flat scrolling backdrop.
    if (bgHills) {
      const bgH = Math.max(140, height * 0.26);
      const horizonY = height * 0.55;
      const bgY = horizonY - bgH + 6;
      const scrollPx = position * 0.04;
      // Fade hills in across the 1.5s straddling the stage boundary.
      const fadeStart = STAGE_MS - 750;
      const fadeEnd = STAGE_MS + 750;
      const cf = elapsed <= fadeStart ? 0 : elapsed >= fadeEnd ? 1 : (elapsed - fadeStart) / (fadeEnd - fadeStart);
      if (cf > 0) drawScrollingStrip(bgHills, scrollPx, bgY, bgH, cf);
    }

    const baseIdx = baseSeg.index;
    const camX = playerX * ROAD_WIDTH;
    const camY = CAM_HEIGHT;
    const camZ = position;
    let maxY = height;
    let x = 0, dx = 0;
    for (let n = 0; n < DRAW_DIST; n++) {
      const seg = segments[(baseIdx + n) % segments.length];
      project(seg.p1, camX - x, camY, camZ);
      project(seg.p2, camX - x - dx, camY, camZ);
      x += dx;
      dx += seg.curve;
      if (seg.p1.camera.z <= CAM_DEPTH || seg.p2.screen.y >= maxY) continue;
      drawSegment(seg, theme);
      maxY = seg.p2.screen.y;
    }

    // Painter's-algorithm pass for billboards, gates, and NPCs. Combine
    // billboards (from segment sprites) and NPCs into a single z-sorted
    // list so nearer ones overdraw farther ones correctly.
    type DrawItem =
      | { kind: "gate";     z: number; seg: Segment }
      | { kind: "bill";     z: number; seg: Segment; sp: Extract<Sprite, { type: "billboard" }> }
      | { kind: "prop";     z: number; seg: Segment; sp: Extract<Sprite, { type: "prop" }> }
      | { kind: "build";    z: number; seg: Segment; sp: Extract<Sprite, { type: "building" }> }
      | { kind: "npc";      z: number; seg: Segment; npc: NpcCar };
    const items: DrawItem[] = [];
    for (let n = 0; n < DRAW_DIST; n++) {
      const seg = segments[(baseIdx + n) % segments.length];
      const segZ = seg.p1.world.z;
      if (seg.p1.camera.z <= CAM_DEPTH) continue;
      for (const sp of seg.sprites) {
        if (sp.type === "gate" && sp.x < 90) items.push({ kind: "gate", z: segZ, seg });
        else if (sp.type === "billboard") items.push({ kind: "bill", z: segZ, seg, sp });
        else if (sp.type === "prop") items.push({ kind: "prop", z: segZ, seg, sp });
        else if (sp.type === "building") items.push({ kind: "build", z: segZ, seg, sp });
      }
    }
    // NPCs in front of the camera within view distance. Pin to the segment's
    // already-projected screen point so NPCs follow the road's curve (and
    // its scaled width) the same way billboards and props do — no more
    // floating-above-the-road bug.
    const NPC_VIEW_SEGS = 8;                       // pop into view inside 8 segments
    const viewDist = NPC_VIEW_SEGS * SEG_LEN;
    const trackLen = segments.length * SEG_LEN;
    for (const npc of npcs) {
      const rel = (npc.z - camZ + trackLen) % trackLen;
      if (rel <= 0 || rel > viewDist) continue;
      const segIdx = (baseIdx + Math.floor(rel / SEG_LEN)) % segments.length;
      const seg = segments[segIdx];
      if (seg.p1.camera.z <= CAM_DEPTH) continue;  // behind camera plane
      items.push({ kind: "npc", z: camZ + rel, seg, npc });
    }
    items.sort((a, b) => b.z - a.z);   // back-to-front

    // Split into "far" and "near" passes. Gates whose world z is within
    // a couple of segments of the camera straddle the player physically
    // (the car drives between the pillars / under the overhead beam) —
    // those need to draw AFTER the player so the gate visually wraps
    // around the car instead of being painted under it. Everything else
    // keeps the original far→near painter's order.
    const NEAR_OVERLAY_DIST = SEG_LEN * 2.5;
    const drawItem = (it: DrawItem) => {
      if (it.kind === "gate") {
        drawGate(it.seg, theme);
      } else if (it.kind === "bill") {
        drawBillboard(it.sp, it.seg.p1.screen);
      } else if (it.kind === "prop") {
        drawProp(it.sp, it.seg.p1.screen);
      } else if (it.kind === "build") {
        drawBuilding(it.sp, it.seg.p1.screen);
      } else {
        drawNpcCar(it.npc, it.seg.p1.screen);
      }
    };
    const nearOverlay: DrawItem[] = [];
    for (const it of items) {
      if (it.kind === "gate" && (it.z - camZ) < NEAR_OVERLAY_DIST) {
        nearOverlay.push(it);
      } else {
        drawItem(it);
      }
    }

    drawPlayerCar();

    // Gates that the player is actively driving under — beam + halo paint
    // over the car so it reads as passing through, not on top of.
    for (const it of nearOverlay) drawItem(it);

    // Nitro speed-blur wash + radial speed-lines (anime style). Lines are
    // re-randomized each frame so they read as motion streaks, not a fixed
    // pattern. Cheap: 24 short strokes per frame, only while nitro fires.
    if (nitroActive && nitro > 0) {
      const rg = ctx!.createRadialGradient(
        width / 2, height * 0.55, Math.min(width, height) * 0.15,
        width / 2, height * 0.55, Math.max(width, height) * 0.7,
      );
      rg.addColorStop(0, "rgba(58,156,255,0)");
      rg.addColorStop(1, "rgba(58,156,255,0.35)");
      ctx!.fillStyle = rg;
      ctx!.fillRect(0, 0, width, height);

      // Speed lines
      const cx = width / 2;
      const cy = height * 0.55;
      const rMax = Math.max(width, height);
      ctx!.save();
      ctx!.globalAlpha = 0.45;
      ctx!.globalCompositeOperation = "lighter";
      ctx!.strokeStyle = "#ffffff";
      ctx!.lineWidth = Math.max(1.5, rMax * 0.0035);
      ctx!.lineCap = "round";
      const N = 24;
      for (let i = 0; i < N; i++) {
        const ang = (i / N) * Math.PI * 2 + Math.random() * 0.18;
        const r1 = rMax * (0.35 + Math.random() * 0.05);
        const r2 = rMax * (0.55 + Math.random() * 0.10);
        ctx!.beginPath();
        ctx!.moveTo(cx + Math.cos(ang) * r1, cy + Math.sin(ang) * r1);
        ctx!.lineTo(cx + Math.cos(ang) * r2, cy + Math.sin(ang) * r2);
        ctx!.stroke();
      }
      // A few cyan accent streaks
      ctx!.strokeStyle = "rgba(108, 214, 255, 0.9)";
      for (let i = 0; i < 6; i++) {
        const ang = Math.random() * Math.PI * 2;
        const r1 = rMax * (0.30 + Math.random() * 0.05);
        const r2 = rMax * (0.55 + Math.random() * 0.12);
        ctx!.beginPath();
        ctx!.moveTo(cx + Math.cos(ang) * r1, cy + Math.sin(ang) * r1);
        ctx!.lineTo(cx + Math.cos(ang) * r2, cy + Math.sin(ang) * r2);
        ctx!.stroke();
      }
      ctx!.restore();
    }
  }

  function start() {
    buildTrack();
    resize();
    position = 0; playerX = 0; speed = MAX_SPEED * 0.2;
    score = 0; nitro = 1; nitroActive = false;
    currentStage = -1;
    misses = 0;
    gatesPassed = 0;   // restart re-opens the speed ramp at 100 mph
    running = true;
    startedAt = performance.now();
    lastFrame = startedAt;
    requestAnimationFrame(frame);
  }
  function stop() { running = false; }

  return {
    start, stop, resize,
    setTilt: (v: number) => { tilt = Math.max(-1, Math.min(1, v)); },
    setNitro: (on: boolean) => { nitroActive = on; },
    getTimeLeft: () => Math.max(0, ROUND_MS - elapsed),
    getNitro: () => nitro,
    getMisses: () => misses,
    getStage: () => Math.max(0, Math.min(THEMES.length - 1, Math.floor(elapsed / STAGE_MS))),
    getThemes: () => THEMES,
    // Dev/QA helpers — safe to ship, not bound to any UI.
    __debugBackdrops: () => ({ city: bgCity, hills: bgHills }),
    __debugSkipToStage: (s: number) => { startedAt -= s * STAGE_MS; },
  };
}
